import { Component, OnInit } from '@angular/core';

import { AppService } from '../../services/app.service';
import { ITableColumn } from '../../shared/table/table.component';
import { IVaccinationDocument } from '../../models/VaccinationDocument';
import { VaccinationDocumentService } from '../../services/vaccination-document.service';
import { VaccinationDocumentApprovalComponent } from './approval.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

interface IFilterOption {
    text: string;
    value: string;
}

@Component({
    selector: 'app-vaccination-document-list',
    templateUrl: './list.component.html'
})
export class VaccinationDocumentListComponent implements OnInit {
    constructor(
        private app: AppService,
        private service: VaccinationDocumentService,
        private modal: NgbModal
    ) { }

    items: IVaccinationDocument[] = [];

    readonly canReview = this.app.currentUser.rights.includes('VACCINATION_DOCUMENT.APPROVE');
    readonly canEdit = this.app.currentUser.rights.includes('VACCINATION_DOCUMENT.EDIT');
    readonly canAdmin = this.app.currentUser.rights.includes('VACCINATION_DOCUMENT.ADMIN');

    readonly columns: ITableColumn[] = [
        { property: 'Type', label: 'vaccinationDocument_lblType', sorts: true },
        { property: 'Expires', label: 'vaccinationDocument_lblExpires', sorts: true, type: 'date' },
        { property: 'OwnerFullName', label: 'vaccinationDocument_lblPerson', sorts: true, cssClass: this.canReview ? '' : 'd-none' },
        { property: 'Status', label: 'vaccinationDocument_lblStatus', sorts: true },
        { width: '1px' }
    ];

    readonly filters: {
        program: { options: IFilterOption[], value: string[] },
        faculty: { options: IFilterOption[], value: string[] },
        person: { options: IFilterOption[], value: string[] }
    } = {
            program: { options: [], value: [] },
            faculty: { options: [], value: [] },
            person: { options: [], value: [] }
        };

    private allItems: IVaccinationDocument[] = [];

    ngOnInit() {
        this.fetch();
    }

    delete(item: IVaccinationDocument) {
        this.app.confirm(this.app.translate('confirmItemDelete'), result => {
            if (!result) return;

            this.app.addLoading(this.service.delete(item.Id)).subscribe(() => {
                this.fetch();
            });
        });
    }

    filter() {
        const program = this.filters.program.value;
        const faculty = this.filters.faculty.value;
        const person = this.filters.person.value;

        if (program.length || faculty.length || person.length) {
            this.items = this.allItems.filter(t => {
                if (person.length && !person.includes(t.OwnerUserName)) return false;
                if (program.length && !t.Affiliations.some(a => program.includes(a.ProgramCode))) return false;
                if (faculty.length && !t.Affiliations.some(a => faculty.includes(a.FacultyCode))) return false;
                return true;
            });
        } else {
            this.items = this.allItems;
        }
    }

    download(item: IVaccinationDocument) {
        this.app.addLoading(this.service.download(item.Id)).subscribe();
    }

    review(item: IVaccinationDocument) {
        const ref = this.modal.open(VaccinationDocumentApprovalComponent);
        ref.componentInstance.document = item;
        ref.result.then(() => {
            this.fetch();
        }, () => { });
    }

    checkReview(item: IVaccinationDocument) {
        return item.Status == 'Submitted' && this.canReview;
    }

    checkDelete(item: IVaccinationDocument) {
        return item.Status == 'Draft' && (this.canAdmin
            || (item.OwnerUserName == this.app.currentUser.username && !item.IsAutoGenerated));
    }

    private fetch() {
        this.app.addLoading(this.service.get()).subscribe(data => {
            this.items = data;
            this.allItems = data;

            const programs: IFilterOption[] = [];
            const faculties: IFilterOption[] = [];
            const persons: IFilterOption[] = [];

            data.forEach(t => {
                if (!persons.some(p => p.value == t.OwnerUserName)) {
                    persons.push({ value: t.OwnerUserName, text: `${t.OwnerFullName} (${t.OwnerUserName})` });
                }

                t.Affiliations.forEach(a => {
                    if (a.ProgramCode && !programs.some(p => p.value == a.ProgramCode)) {
                        programs.push({ value: a.ProgramCode, text: `${a.ProgramCode} ${a.ProgramName}` });
                    }

                    if (a.FacultyCode && !faculties.some(p => p.value == a.FacultyCode)) {
                        faculties.push({ value: a.FacultyCode, text: `${a.FacultyCode} ${a.FacultyName}` });
                    }
                });
            });

            faculties.sort((a, b) => a.text.localeCompare(b.text));
            programs.sort((a, b) => a.text.localeCompare(b.text));
            persons.sort((a, b) => a.text.localeCompare(b.text));
            
            this.filters.faculty.options = faculties;
            this.filters.program.options = programs;
            this.filters.person.options = persons;
        });
    }
}
